<template>
  <div class="space-y-3">
    <!-- Staged entries, one row each. Whether a closed day costs a day of leave
         is THE question about it, so it is a switch on the row itself rather
         than something decided once at the moment of adding. -->
    <ul v-if="modelValue.length" class="space-y-2">
      <li
        v-for="(item, i) in modelValue"
        :key="`${item.name}-${item.startDate}`"
        data-test="wizard-company-holiday"
        class="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg bg-surface px-3 py-2 ring-1 ring-inset ring-[var(--pill-ring)]"
      >
        <span class="min-w-0 flex-1 text-sm text-heading">
          <span class="font-medium">{{ item.name }}</span>
          <span class="text-secondary"> · {{ periodLabel(item) }}</span>
          <span v-if="item.isHalfDay" class="text-secondary"> · {{ t('onboarding.wizard.sections.time_tracking.companyHolidays.halfDay') }}</span>
          <span v-if="item.recursYearly" class="text-secondary"> · {{ t('onboarding.wizard.sections.time_tracking.companyHolidays.yearly') }}</span>
        </span>
        <Checkbox
          :model-value="item.deductsFromLeave"
          data-test="wizard-company-holiday-deducts"
          :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.deductsFromLeave')"
          @update:model-value="(v: boolean) => setDeducts(i, v)"
        />
        <IconButton variant="ghost" size="sm" :label="t('onboarding.actions.remove')" @click="removeAt(i)">
          <LumiIcon name="remove" />
        </IconButton>
      </li>
    </ul>
    <EmptyState v-else :title="t('onboarding.wizard.sections.time_tracking.companyHolidays.empty')" />

    <!-- The two days nearly every company closes early on: one press each. -->
    <div v-if="suggestions.length" class="flex flex-wrap items-center gap-2">
      <span class="text-xs text-secondary">{{ t('onboarding.wizard.sections.time_tracking.companyHolidays.suggestions') }}</span>
      <Button
        v-for="s in suggestions"
        :key="s.startDate"
        variant="secondary"
        size="sm"
        :data-test="`wizard-company-holiday-suggest-${s.startDate.slice(5)}`"
        @click="append(s)"
      >
        {{ s.name }} · {{ t('onboarding.wizard.sections.time_tracking.companyHolidays.halfDay') }}
      </Button>
    </div>

    <!-- One more entry: a name, a day (or a range), and what kind of day it is. -->
    <div class="space-y-3 rounded-lg border border-default p-3">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.name')">
          <Input v-model="draft.name" data-test="wizard-company-holiday-name" :placeholder="t('onboarding.wizard.sections.time_tracking.companyHolidays.namePlaceholder')" maxlength="120" />
        </Field>
        <Field :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.from')">
          <DateField v-model="draft.startDate" type="date" :locale="uiLocale(locale)" />
        </Field>
        <Field :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.to')">
          <DateField v-model="draft.endDate" type="date" :locale="uiLocale(locale)" />
        </Field>
      </div>
      <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
        <Checkbox v-model="draft.isHalfDay" :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.halfDay')" />
        <Checkbox v-model="draft.recursYearly" :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.yearly')" />
        <Checkbox v-model="draft.deductsFromLeave" :label="t('onboarding.wizard.sections.time_tracking.companyHolidays.deductsFromLeave')" />
        <div class="flex-1" />
        <Button
          data-test="wizard-company-holiday-add"
          variant="secondary"
          :disabled="!draftValid"
          :aria-label="t('onboarding.actions.add')"
          @click="addDraft"
        >
          <LumiIcon name="add" class="h-4 w-4" />
        </Button>
      </div>
      <p v-if="rangeReversed" class="text-xs text-danger-text">{{ t('onboarding.wizard.sections.time_tracking.companyHolidays.rangeReversed') }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button, Checkbox, DateField, EmptyState, Field, Input, IconButton, LumiIcon } from '@lumi/ui';
import { uiLocale } from '@lumi/app-kit';
import { companyHolidayKey, type StagedCompanyHoliday } from '../../utils/companyHolidays';

// The wizard's company-holiday list (Betriebsferien): the days the company
// itself closes, asked for at setup because they decide two things from day
// one — nobody owes hours on them, and each one either costs everybody a day of
// leave or is granted on top. Staged only, like every other wizard answer
// (ADR 0032): nothing reaches Timecard until Finish replays the list.
const props = defineProps<{ modelValue: StagedCompanyHoliday[] }>();
const emit = defineEmits<{ (e: 'update:modelValue', value: StagedCompanyHoliday[]): void }>();

const { t, locale } = useI18n();

const emptyDraft = (): StagedCompanyHoliday => ({
  name: '',
  startDate: '',
  endDate: '',
  isHalfDay: false,
  recursYearly: false,
  deductsFromLeave: false,
});
const draft = reactive<StagedCompanyHoliday>(emptyDraft());

const rangeReversed = computed(() => !!draft.startDate && !!draft.endDate && draft.endDate < draft.startDate);
const draftValid = computed(() => !!draft.name.trim() && !!draft.startDate && !rangeReversed.value);

// Christmas Eve and New Year's Eve, half a day each, every year — offered until
// they are on the list. Anchored in the running year: a yearly entry applies
// from the year it is entered for.
const suggestions = computed<StagedCompanyHoliday[]>(() => {
  const year = new Date().getFullYear();
  const presets: StagedCompanyHoliday[] = [
    { name: t('onboarding.wizard.sections.time_tracking.companyHolidays.christmasEve'), startDate: `${year}-12-24`, endDate: `${year}-12-24`, isHalfDay: true, recursYearly: true, deductsFromLeave: false },
    { name: t('onboarding.wizard.sections.time_tracking.companyHolidays.newYearsEve'), startDate: `${year}-12-31`, endDate: `${year}-12-31`, isHalfDay: true, recursYearly: true, deductsFromLeave: false },
  ];
  return presets.filter((p) => !props.modelValue.some((v) => v.startDate.slice(5) === p.startDate.slice(5)));
});

function formatDay(day: string, withYear: boolean): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(locale.value, {
    day: '2-digit',
    month: '2-digit',
    ...(withYear ? { year: 'numeric' as const } : {}),
  });
}

// "24.12." for a day that repeats every year, "03.08.2027 – 14.08.2027" for a
// range that happens once.
function periodLabel(item: StagedCompanyHoliday): string {
  const withYear = !item.recursYearly;
  return item.startDate === item.endDate
    ? formatDay(item.startDate, withYear)
    : `${formatDay(item.startDate, withYear)} – ${formatDay(item.endDate, withYear)}`;
}

function append(entry: StagedCompanyHoliday) {
  // The same name on the same day is the same entry: adding it twice adds nothing.
  if (props.modelValue.some((v) => companyHolidayKey(v) === companyHolidayKey(entry))) return;
  emit('update:modelValue', [...props.modelValue, entry]);
}

function addDraft() {
  if (!draftValid.value) return;
  append({ ...draft, name: draft.name.trim(), endDate: draft.endDate || draft.startDate });
  Object.assign(draft, emptyDraft());
}

function setDeducts(index: number, deductsFromLeave: boolean) {
  emit(
    'update:modelValue',
    props.modelValue.map((item, i) => (i === index ? { ...item, deductsFromLeave } : item)),
  );
}

function removeAt(index: number) {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
}

// Leaving the section keeps an entry that was filled in but never added — the
// ➕ is easy to miss, and silently dropping it is what made "Weiter" look
// broken for the other list editors (#546).
defineExpose({ flush: addDraft });
</script>
